import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@predoc/cow';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PDFViewer } from '@/components';
import { formatDisplayDate, formatISODate } from '@/utils/date';
import { downloadFromUrl, sanitizeFilename } from '@/utils/downloadUtils';
import { useEfaxOutputDocumentLink } from '../hooks/efax-output-document-link';
import { useEfaxSourcePdfLink } from '../hooks/efax-source-pdf-link';
import type { EfaxQueueItem } from '../types';
import { getPatientDisplayName } from '../utils/efax-item-helpers';
import { hasExtractedValue } from './cells/efax-cell-utils';
import { EfaxStatusBadge } from './EfaxStatusBadge';

type ViewerDocumentKind = 'source' | 'output';

interface ViewerDocumentSelection {
  kind: ViewerDocumentKind;
  id: string;
  title: string;
}

interface EfaxDocumentViewerProps {
  item: EfaxQueueItem | null;
  open: boolean;
  onClose: () => void;
  initialDocumentId?: string;
}

const LoadingSlot = () => (
  <div className="flex h-full items-center justify-center">
    <Loader2
      className="text-muted-foreground size-10 animate-spin"
      aria-label="Loading document"
    />
  </div>
);

export const EfaxDocumentViewer = ({
  item,
  open,
  onClose,
  initialDocumentId,
}: EfaxDocumentViewerProps) => {
  const documents = useMemo((): ViewerDocumentSelection[] => {
    if (!item) return [];

    const sourceDocuments: ViewerDocumentSelection[] = item.document
      .sourcePdfAvailable
      ? [
          {
            kind: 'source',
            id: item.recordUploadId,
            title: item.document.fileName,
          },
        ]
      : [];

    const outputDocuments = item.output.documents.map((document) => ({
      kind: 'output' as const,
      id: document.documentId,
      title: document.name?.trim() ? document.name : document.documentType,
    }));

    return [...sourceDocuments, ...outputDocuments];
  }, [item]);

  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedDocumentId(initialDocumentId ?? documents[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.recordUploadId, initialDocumentId]);

  const selectedDocument =
    documents.find((document) => document.id === selectedDocumentId) ??
    documents[0];

  const isSourceSelected = selectedDocument?.kind === 'source';

  const sourceLinkQuery = useEfaxSourcePdfLink(
    {
      recordUploadId: item?.recordUploadId ?? '',
    },
    {
      enabled:
        open && !!item && isSourceSelected && item.document.sourcePdfAvailable,
    },
  );

  const outputLinkQuery = useEfaxOutputDocumentLink(
    {
      recordUploadId: item?.recordUploadId ?? '',
      documentId: selectedDocument?.id ?? '',
    },
    {
      enabled:
        open &&
        !!item &&
        !!selectedDocument &&
        selectedDocument.kind === 'output',
    },
  );

  const activeLinkQuery = isSourceSelected ? sourceLinkQuery : outputLinkQuery;
  const signedUrl = activeLinkQuery.data?.signedUrl;
  const isLoadingDocument = activeLinkQuery.isFetching;
  const hasDocumentError = activeLinkQuery.isError;

  const handleDownload = async () => {
    if (!selectedDocument) return;

    const result = await activeLinkQuery.refetch();
    const url = result.data?.signedUrl;
    if (!url) {
      toast.error('Unable to download this document. Please try again.');
      return;
    }

    await downloadFromUrl(
      url,
      `${sanitizeFilename(selectedDocument.title)}.pdf`,
    );
  };

  const headerTitle = selectedDocument?.title ?? item?.document.fileName ?? '';

  return (
    <Sheet open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <SheetContent
        showCloseButton={false}
        className="inset-0 h-screen w-screen max-w-none gap-0 border-0 p-0 sm:max-w-none"
      >
        {item ? (
          <>
            <SheetHeader className="sr-only">
              <SheetTitle>{headerTitle}</SheetTitle>
            </SheetHeader>
            <div className="flex min-h-16 items-center justify-between gap-4 border-b bg-white px-6">
              <div className="flex min-w-0 items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close eFax document viewer"
                >
                  <ArrowLeft className="size-5" />
                </Button>
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-semibold">
                    {headerTitle}
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    {item.documentId} · Received{' '}
                    {formatDisplayDate(item.receivedAt, 'MMM d, yyyy h:mm a') ??
                      ''}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <EfaxStatusBadge
                  status={item.status}
                  details={item.errorSummary ?? undefined}
                  onViewRawDocument={
                    item.document.sourcePdfAvailable
                      ? () => setSelectedDocumentId(item.recordUploadId)
                      : undefined
                  }
                />
                <span className="text-muted-foreground text-sm">
                  {getPatientDisplayName(item.patient)}
                  {hasExtractedValue(item.patient?.dateOfBirth)
                    ? ` · DOB ${formatISODate(item.patient?.dateOfBirth)}`
                    : ''}
                </span>
                <Button
                  variant="outline"
                  className="gap-1.5"
                  disabled={
                    !selectedDocument || isLoadingDocument || hasDocumentError
                  }
                  onClick={handleDownload}
                >
                  <Download className="size-4" />
                  Download
                </Button>
              </div>
            </div>

            <main className="min-h-0 flex-1 bg-neutral-800">
              {!selectedDocument ? (
                <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                  No document selected
                </div>
              ) : isLoadingDocument ? (
                <LoadingSlot />
              ) : hasDocumentError ? (
                <div className="text-muted-foreground flex h-full items-center justify-center px-6 text-center text-sm">
                  Unable to load this document. Please try again.
                </div>
              ) : signedUrl ? (
                <PDFViewer file={signedUrl} className="h-full" />
              ) : (
                <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                  This document has no viewable file.
                </div>
              )}
            </main>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
